import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MyLearningStatuEnum } from '@edtech-ui/constants/pro-courses-status.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { StudentCoursesService } from '@edtech-ui/core/services/student-courses/student-courses.service';
import { StudentTopicProgressStatus } from '@edtech-ui/constants/student-course-enrollment.enum';
import { StudentProgressCourseData } from '@edtech-ui/core/models/student-courses-v2/student-course-detail-progress';
import { environment } from 'environments/environment';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

@Component({
	selector: 'app-course-description',
	standalone: true,
	imports: [MatToolbarModule, CommonModule, MatProgressSpinnerModule, HasPermissionDirective],
	templateUrl: './course-description.component.html',
	styleUrl: './course-description.component.scss'
})
export class CourseDescriptionComponent implements OnInit, OnDestroy {

	courseId!: string;
	classId = environment.classroomId;
	unitId: string | null = null;
	topicId: string | null = null;
	selectedUnit: any = null;
	selectedTopic: any = null;
	isLoading = false;
	subscription = new Subscription();
	courseDetails: StudentProgressCourseData | null = null;
	StudentTopicProgressStatus = StudentTopicProgressStatus;
	selectedTab: MyLearningStatuEnum = MyLearningStatuEnum.LESSON;
	myLearningTabStatus = MyLearningStatuEnum;
	PermissionForStudents = PermissionForStudents;

	selectTab(tab: MyLearningStatuEnum) {
		this.selectedTab = tab
	}

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private _toastr: ToastrService,
		private _studentCourseService: StudentCoursesService,
		private _authService: AuthServiceService
	) { }

	ngOnInit() {

		if (!this._authService.hasPermission(PermissionForStudents.COURSE_VIEW)) {
			this._toastr.error('You do not have permission to view this course');
			this.router.navigate(['/student-dashboard']);
			return;
		}
		this.classId = Number(this.route.parent?.snapshot.paramMap.get('classId')!);
		this.courseId = this.route.parent?.snapshot.paramMap.get('courseId')!;

		this.subscription.add(
			this.route.paramMap.subscribe(params => {
				this.unitId = params.get('unitId');
				this.topicId = params.get('topicId');
				if (this.courseDetails && this.unitId && this.topicId) {
					this.resolveUnitAndTopic();
				}
			}));

		this.subscription.add(
			this._studentCourseService.courseDetails$.subscribe(details => {
				if (details) {
					this.courseDetails = details;
					this.tryResolveLesson();
				}
			})
		);
		this.getCourseByCourseId();
	}

	getAllLessons() {
		const lessons: any[] = [];
		this.courseDetails?.units.forEach((unit: any) => {
			unit.topics.forEach((topic: any) => { lessons.push({ sectionId: unit.unit_id, lessonId: topic.topic_id }) });
		});
		return lessons;
	}

	goToNextLesson() {
		if (!this.topicId || !this.courseDetails) return;
		const lessons = this.getAllLessons();
		const index = this.getCurrentLessonIndex();
		if (index === -1 || index === lessons.length - 1) return;

		const next = lessons[index + 1];

		const goNext = () => this.router.navigate(['course-description', next.sectionId, next.lessonId], { relativeTo: this.route.parent });

		if (this.courseDetails) {
			this.courseDetails.progress_percentage = this.calculateProgressPercentage(this.courseDetails);
			this._studentCourseService.setCourseDetails({ ...this.courseDetails });
		}

		if (this.selectedTopic?.status !== StudentTopicProgressStatus.COMPLETED) {
			this.patchTopicProgress(() => goNext());
		}
		else {
			goNext();
		}
	}

	goToPreviousLesson() {
		const lessons = this.getAllLessons();

		const currentIndex = lessons.findIndex(l => l.sectionId.toString() === this.unitId && l.lessonId.toString() === this.topicId);

		if (currentIndex <= 0) return;
		const prevLesson = lessons[currentIndex - 1];
		this.router.navigate(['course-description', prevLesson.sectionId, prevLesson.lessonId], { relativeTo: this.route.parent });
	}

	getCourseByCourseId() {
		this.isLoading = true;
		this.subscription.add(
			this._studentCourseService.getStudentCoursesProgress(this.courseId).pipe(
				tap(response => {
					if (response.Success) {
						this.courseDetails = response.Data;
						this._studentCourseService.setCourseDetails(response.Data);

						if (!this.isLessonRoute()) return;

						if (this.unitId && this.topicId) {
							this.resolveUnitAndTopic();
						}

						if (!this.unitId && !this.topicId) {
							this.openFirstPendingLesson();
						}
					} else {
						this._toastr.error(response.Message);
					}
				}),
				finalize(() => {
					this.isLoading = false;
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while fetching courses details');
					return of(null);
				})
			).subscribe()
		)
	}

	resolveUnitAndTopic() {
		if (!this.courseDetails || !this.unitId || !this.topicId) return;
		this.selectedUnit = this.courseDetails.units.find(unit => unit.unit_id === this.unitId);
		if (!this.selectedUnit) return;
		this.selectedTopic = this.selectedUnit.topics.find((topic: any) => topic.topic_id === this.topicId);
	}

	selectTopic(unitId: string, topicId: string): void {
		this.router.navigate(['course-description', unitId, topicId], { relativeTo: this.route.parent });
	}

	isFirstLesson(): boolean {
		const lessons = this.getAllLessons();
		const currentIndex = lessons.findIndex(l => l.sectionId.toString() === this.unitId?.toString() && l.lessonId.toString() === this.topicId?.toString());
		return currentIndex <= 0;
	}

	isLastLesson(): boolean {
		if (!this.courseDetails) return false;
		const lastUnit = this.courseDetails.units.at(-1);
		const lastTopic = lastUnit?.topics.at(-1);
		return this.unitId?.toString() === lastUnit?.unit_id?.toString() && this.topicId?.toString() === lastTopic?.topic_id?.toString();
	}

	patchTopicProgress(callback?: () => void) {
		if (!this.topicId || !this.selectedUnit || !this.selectedTopic) return;

		const payload = {
			topic_id: this.topicId,
			status: StudentTopicProgressStatus.COMPLETED
		};

		this.subscription.add(
			this._studentCourseService.patchTopicProgress(this.courseId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this.selectedTopic.status = StudentTopicProgressStatus.COMPLETED;

						const topicIndex = this.selectedUnit.topics.findIndex((t: any) => t.topic_id === this.topicId);
						if (topicIndex !== -1) {
							this.selectedUnit.topics[topicIndex].status = StudentTopicProgressStatus.COMPLETED;
						}

						this.checkUnitCompletion(this.selectedUnit);
						this.courseDetails!.progress_percentage = this.calculateProgressPercentage(this.courseDetails!);
						this._studentCourseService.setCourseDetails({ ...this.courseDetails! });
						if (callback) callback();
					} else {
						this._toastr.error(response.Message);
						this.router.navigate(['student-dashboard/course-details', this.courseId]);
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong. Please try again');
					return of(null);
				})
			).subscribe()
		);
	}


	checkUnitCompletion(unit: any) {
		if (!unit || !unit.topics) return false;
		const allTopicsDone = unit.topics.every((topic: any) => topic.status === StudentTopicProgressStatus.COMPLETED);
		unit.status = allTopicsDone ? 'COMPLETED' : 'IN_PROGRESS';
		return allTopicsDone;
	}

	markLastLessonCompleted() {
		if (!this.isLastLesson() || this.selectedTopic?.status === StudentTopicProgressStatus.COMPLETED) {
			return;
		}
		this.patchTopicProgress(() => {
			this._toastr.success('Course completed Successfully');
		});
	}

	goToProCourses() {
		this.router.navigate(['student-dashboard/pro-course']);
	}

	getCurrentLessonIndex(): number {
		const lessons = this.getAllLessons();
		return lessons.findIndex(l => l.sectionId.toString() === this.unitId?.toString() && l.lessonId.toString() === this.topicId?.toString());
	}

	openFirstPendingLesson(): void {
		if (!this.isLessonRoute()) return;
		if (!this.courseDetails?.units?.length) return;

		for (const unit of this.courseDetails.units) {

			const pendingTopic = unit.topics.find((t: any) => t.status !== StudentTopicProgressStatus.COMPLETED);

			if (pendingTopic) {
				this.selectedUnit = unit;
				this.selectedTopic = pendingTopic;
				this.unitId = unit.unit_id;
				this.topicId = pendingTopic.topic_id;

				this.router.navigate(['course-description', unit.unit_id, pendingTopic.topic_id], { relativeTo: this.route.parent });
				return;
			}
		}

		const lastUnit = this.courseDetails.units.at(-1);
		const lastTopic = lastUnit?.topics.at(-1);

		if (lastUnit && lastTopic) {
			this.selectedUnit = lastUnit;
			this.selectedTopic = lastTopic;
			this.unitId = lastUnit.unit_id;
			this.topicId = lastTopic.topic_id;
			this.router.navigate(['course-description', lastUnit.unit_id, lastTopic.topic_id], { relativeTo: this.route.parent });
		}
	}

	private isLessonRoute(): boolean {
		return this.router.url.includes('course-description');
	}

	calculateProgressPercentage(course: StudentProgressCourseData): number {
		const totalTopics = course.units.reduce((sum, unit) => sum + (unit.topics?.length || 0), 0);
		const completedTopics = course.units.reduce(
			(sum, unit) => sum + (unit.topics?.filter(t => t.status === StudentTopicProgressStatus.COMPLETED).length || 0), 0);
		return totalTopics ? Math.round((completedTopics / totalTopics) * 100) : 0;
	}

	private tryResolveLesson(): void {
		if (!this.courseDetails || !this.unitId || !this.topicId) return;
		this.resolveUnitAndTopic();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}